import { useState, useCallback, useEffect } from 'react'

import { useI18n } from '../../../shared/i18n/useI18n.js'

import { useApp } from '../../../app/AppContext.jsx'
import { useCatalog } from '../context/CatalogContext.jsx'

import { findEntranceTierInProduct, parsePricesVisibility } from '../../../shared/utils/index.js'
import { FETCH_STATUS } from '../../../shared/constants/fetchStatus.js'

import { isProductAccessible } from '../utils/unitTableSettings.js'
import { VIEW_MODE } from '../utils/viewModes.js'

import { CatalogFilters }       from './CatalogFilters/CatalogFilters.jsx'
import { CatalogFiltersSheet }  from './CatalogFilters/CatalogFiltersSheet.jsx'
import { CatalogSortRow }       from './CatalogBar/CatalogSortRow.jsx'
import { CatalogStatusRow }     from './CatalogBar/CatalogStatusRow.jsx'
import { ProductCards }         from './ProductCards/ProductCards.jsx'
import { ProductList }          from './ProductList/ProductList.jsx'
import { ProductTiles }         from './ProductTiles/ProductTiles.jsx'

import styles from './CatalogView.module.scss'

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * Catalog domain view.
 * Renders the production filter stack, status row, sort row and product list/grid.
 */
export function CatalogView() {
  const { t } = useI18n();

  const { 
    activeProduct, 
    appCapabilities,
    logoSrc, 
    isMobile, 
    isFavorite, 
    panelSettings, 
    projectConfig,
    selectProductHandler,
    sectionRefs,
    toggleFavorite, 
    windowData, 
  } = useApp()
  const {
    products,
    catalogCapabilities,
    catalogProducts,
    productsStatus,
    productsError,
    filteredProducts,
    compartmentsError,
    sortConfig,
    setSortConfig,
    activeFiltersConfig,
    fullFiltersRange,
    defaultFiltersConfig,
    setActiveFiltersConfig,
    activeFiltersCount,
    resetFilters,
    restorePreReset,
    canRestorePreReset,
    isFiltersSheetOpen,
    setIsFiltersSheetOpen,
    tiers,
  } = useCatalog()

  const initialViewMode = isMobile ? catalogCapabilities?.loadViewModeMobile : catalogCapabilities?.loadViewModeDesktop
  const [viewMode, setViewMode] = useState(() => initialViewMode)
  const [hasUserChangedViewMode, setHasUserChangedViewMode] = useState(false)

  useEffect(() => {
    if (!hasUserChangedViewMode) {
      setViewMode(initialViewMode)
    }
  }, [hasUserChangedViewMode, initialViewMode])

  const handleFilterChange = useCallback((id, value) => {
    setActiveFiltersConfig(prev => ({ ...prev, [id]: value }))
  }, [setActiveFiltersConfig])

  const handleViewModeChange = useCallback((mode) => {
    setHasUserChangedViewMode(true)
    setViewMode(mode)
  }, [])

  const handleProductClick = useCallback(p => {
    const key = p?.uuid ?? p?.id_cad ?? null
    if (!key) return
    const isAccessible = isProductAccessible(p, {reservedUnitsAccessible, soldUnitsAccessible});
    if (!isAccessible) return;

    const entranceTier = findEntranceTierInProduct(p)?.ref_tier ?? null

    selectProductHandler(key, {
      buildingId: p?.building_id ?? null,
      refTier: entranceTier ?? p?.ref_tier ?? null,
    })
  }, [selectProductHandler])

  const hasMultipleBuildings = catalogCapabilities?.hasMultipleBuildings
  const hasMultipleGates     = catalogCapabilities?.hasMultipleGates

  // Price visibility — parsed once; forwarded to all views that render prices
  const pricesVisibility = {
    pricesVisible:         catalogCapabilities?.pricesVisible         !== false,
    pricesSoldVisible:     catalogCapabilities?.pricesSoldVisible     !== false,
    pricesReservedVisible: catalogCapabilities?.pricesReservedVisible !== false,
  }

  // const listColumns = catalogCapabilities?.listColumns || []
  // const cardDataItems = catalogCapabilities?.cardDataItems || []
  // const tileDataItems = catalogCapabilities?.tileDataItems || []
  const { listColumns, cardDataItems, tileDataItems } = catalogCapabilities
  const stickyColumns = catalogCapabilities?.listViewStickyColumnsCount

  const { 
    reservedUnitsAccessible,
    reservedUnitsPlanVisible,
    soldUnitsAccessible,
    soldUnitsPlanVisible,
  } = appCapabilities

  const noDataText = t('catalog.no_results')
  // ── Loading / error states ──────────────────────────────────────────────

  if (productsStatus === FETCH_STATUS.LOADING) {
    return <div>[CatalogView] Loading products…</div>
  }

  if (productsStatus === FETCH_STATUS.ERROR) {
    return (
      <div>
        [CatalogView] Products failed to load: {productsError?.message ?? 'unknown error'}
      </div>
    )
  }

  // ── Ready ───────────────────────────────────────────────────────────────

  return (
    <section ref={sectionRefs.catalog} className={styles.view}>
      {compartmentsError && (
        <p>[WARN] compartments.json failed: {compartmentsError.message} — external_areas filter disabled</p>
      )}

      {/* ── Filters main bar (desktop/tablet) ── */}
      {!isMobile && (
        <CatalogFilters />
      )}

      {/* ── Filters sheet (mobile) ── */}
      {isMobile && activeFiltersConfig && fullFiltersRange && (
        <CatalogFiltersSheet
          activeFiltersCount={activeFiltersCount}
          canRestorePreReset={canRestorePreReset}
          catalogFilters={catalogCapabilities?.catalogFilters}
          defaultFiltersConfig={defaultFiltersConfig}
          filteredCount={filteredProducts.length}
          filtersConfig={activeFiltersConfig}
          fullFiltersRange={fullFiltersRange}
          isOpen={isFiltersSheetOpen}
          onClose={() => setIsFiltersSheetOpen(false)}
          onFilterChange={handleFilterChange}
          onOpen={() => setIsFiltersSheetOpen(true)}
          onReset={resetFilters}
          onRestorePreReset={restorePreReset}
          totalCount={catalogProducts.length}
        />
      )}

      {/* ── Status row (desktop / tablet only — mobile uses the sheet footer) ── */}
      {!isMobile && (
        <CatalogStatusRow
          filteredCount={filteredProducts.length}
          totalCount={catalogProducts.length}
          activeFiltersCount={activeFiltersCount}
          canRestorePreReset={canRestorePreReset}
          onReset={resetFilters}
          onRestorePreReset={restorePreReset}
        />
      )}

      {/* ── Sort row ── */}
      <CatalogSortRow
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      {/* ── Product list / cards / tiles ── */}
      <div className={styles.listWrapper}>
        {viewMode === VIEW_MODE.LIST && (
          <ProductList
            activeActions={catalogCapabilities?.activeCatalogActions}
            activeProductKey={activeProduct}
            filteredProducts={filteredProducts}
            hasMultipleBuildings={hasMultipleBuildings}
            hasMultipleGates={hasMultipleGates}
            isFavorite={isFavorite}
            listColumns={listColumns}
            noDataText={noDataText}
            onProductClick={handleProductClick}
            onSortChange={setSortConfig}
            pricesVisibility={pricesVisibility}
            reservedUnitsAccessible={reservedUnitsAccessible}
            soldUnitsAccessible={soldUnitsAccessible}
            sortConfig={sortConfig}
            stickyColumns={stickyColumns}
            tiers={tiers}
            toggleFavorite={toggleFavorite}
            // unitSheetAccessibility={unitSheetAccessibility}
            viewMode={VIEW_MODE.LIST}
          
            allProducts={products}
            panelSettings={panelSettings}
          />
        )}
        {viewMode === VIEW_MODE.TILES && (
          <ProductTiles
            panelSettings={panelSettings}
            isMobile={isMobile}
            tiers={tiers}
            projectDataUrl={windowData?.projectDataUrl ?? null}
            logoSrc={logoSrc}
            hasSimilarSection={projectConfig?.app_config?.website?.sections?.similar === true}
            use3dThumbs={false}
            
            
            activeActions={catalogCapabilities?.activeCatalogActions}
            activeProductKey={activeProduct}
            tileDataItems={tileDataItems}
            filteredProducts={filteredProducts}
            isFavorite={isFavorite}
            noDataText={noDataText}
            onProductClick={handleProductClick}
            pricesVisibility={pricesVisibility}
            reservedUnitsAccessible={reservedUnitsAccessible}
            reservedUnitsPlanVisible={reservedUnitsPlanVisible}
            soldUnitsAccessible={soldUnitsAccessible}
            soldUnitsPlanVisible={soldUnitsPlanVisible}
            toggleFavorite={toggleFavorite}
          />
        )}
        {viewMode === VIEW_MODE.CARDS && (
          <ProductCards
            activeActions={catalogCapabilities?.activeCatalogActions}
            activeProductKey={activeProduct}
            cardDataItems={cardDataItems}
            filteredProducts={filteredProducts}
            isFavorite={isFavorite}
            noDataText={noDataText}
            onProductClick={handleProductClick}
            pricesVisibility={pricesVisibility}
            reservedUnitsAccessible={reservedUnitsAccessible}
            soldUnitsAccessible={soldUnitsAccessible}
            tiers={tiers}
            toggleFavorite={toggleFavorite}


            panelSettings={panelSettings}
          />
        )}
      </div>
    </section>
  )
}